import { X, Headphones, Phone, Loader2, Volume2, Copy, AlertCircle, ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../lib/utils'
import { useMonitor } from '../call-staging/useMonitor'
import { useState, useEffect } from 'react'

interface ConferenceMonitorProps {
  callId: string
  onClose: () => void
}

export function ConferenceMonitor({ callId, onClose }: ConferenceMonitorProps) {
  const { t } = useTranslation('simulations')
  const monitor = useMonitor()
  const [joinMethod, setJoinMethod] = useState<'browser' | 'phone'>('browser')
  const [copied, setCopied] = useState(false)

  // Auto-start browser monitoring on mount
  useEffect(() => {
    if (joinMethod === 'browser' && monitor.state === 'idle') {
      monitor.monitor(callId)
    }
  }, [callId, joinMethod, monitor])

  const handleCopyConferenceName = () => {
    if (monitor.conferenceName) {
      navigator.clipboard.writeText(monitor.conferenceName)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleDisconnect = () => {
    monitor.disconnect()
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="relative w-full max-w-lg rounded-lg border border-border bg-surface-card p-6 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-text-primary">
            {t('monitor.title')}
          </h3>
          <button
            onClick={handleDisconnect}
            className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-surface-elevated hover:text-text-primary"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Join method tabs */}
        <div className="mt-4 flex gap-2 rounded-lg bg-surface-elevated p-1">
          <button
            onClick={() => setJoinMethod('browser')}
            className={cn(
              'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              joinMethod === 'browser'
                ? 'bg-surface-card text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary',
            )}
          >
            <Headphones className="mx-auto h-4 w-4 mb-1" />
            {t('monitor.browser')}
          </button>
          <button
            onClick={() => setJoinMethod('phone')}
            className={cn(
              'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              joinMethod === 'phone'
                ? 'bg-surface-card text-text-primary shadow-sm'
                : 'text-text-secondary hover:text-text-primary',
            )}
          >
            <Phone className="mx-auto h-4 w-4 mb-1" />
            {t('monitor.phone')}
          </button>
        </div>

        {/* Browser monitoring */}
        {joinMethod === 'browser' && (
          <div className="mt-6 space-y-4">
            {monitor.isConnecting && (
              <div className="flex flex-col items-center gap-3 rounded-lg bg-surface-elevated px-4 py-6">
                <Loader2 className="h-8 w-8 animate-spin text-info" />
                <p className="text-sm text-text-secondary">
                  {t('monitor.connecting')}
                </p>
              </div>
            )}

            {monitor.isConnected && (
              <div className="space-y-3">
                <div className="flex items-center gap-3 rounded-lg bg-success/10 px-4 py-3 border border-success/20">
                  <span className="flex h-2 w-2">
                    <span className="absolute h-2 w-2 animate-ping rounded-full bg-success opacity-75" />
                    <span className="relative h-2 w-2 rounded-full bg-success" />
                  </span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-success">
                      {t('monitor.connected')}
                    </p>
                    <p className="mt-0.5 text-xs text-text-muted">
                      {t('monitor.youAreMuted')}
                    </p>
                  </div>
                  <Volume2 className="h-5 w-5 text-success" />
                </div>

                <button
                  onClick={handleDisconnect}
                  className="w-full rounded-lg bg-error px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-error/80"
                >
                  {t('monitor.stopListening')}
                </button>
              </div>
            )}

            {monitor.state === 'error' && monitor.error && (
              <div className="space-y-3">
                {/* Check if it's a configuration error */}
                {monitor.error.includes('Twilio monitoring not configured') ||
                 monitor.error.includes('Missing:') ? (
                  <div className="rounded-lg bg-warning/10 px-4 py-4 border border-warning/20 space-y-3">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-warning shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-warning">
                          {t('monitor.configRequired')}
                        </p>
                        <p className="mt-1 text-xs text-text-secondary">
                          {t('monitor.configBody')}
                        </p>
                      </div>
                    </div>

                    <div className="bg-surface-elevated rounded-md px-3 py-2.5 space-y-2">
                      <p className="text-xs font-medium text-text-primary">{t('monitor.quickSetup')}</p>
                      <ol className="text-xs text-text-secondary space-y-1.5 pl-4 list-decimal">
                        <li>
                          <a
                            href="https://console.twilio.com/us1/develop/api-keys"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-info hover:underline inline-flex items-center gap-1"
                          >
                            {t('monitor.step1')}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </li>
                        <li>
                          <a
                            href="https://console.twilio.com/us1/develop/voice/manage/twiml-apps"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-info hover:underline inline-flex items-center gap-1"
                          >
                            {t('monitor.step2')}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        </li>
                        <li>{t('monitor.step3')}</li>
                        <li>{t('monitor.step4')}</li>
                      </ol>
                    </div>

                    <details className="text-xs">
                      <summary className="cursor-pointer text-text-muted hover:text-text-primary">
                        {t('monitor.showEnvVars')}
                      </summary>
                      <div className="mt-2 bg-surface-card rounded px-3 py-2 font-mono text-text-secondary space-y-1">
                        <div>TWILIO_API_KEY_SID=SK...</div>
                        <div>TWILIO_API_KEY_SECRET=...</div>
                        <div>TWILIO_TWIML_APP_SID=AP...</div>
                      </div>
                    </details>
                  </div>
                ) : (
                  <div className="rounded-lg bg-error/10 px-4 py-3 border border-error/20">
                    <p className="text-sm font-medium text-error">
                      {t('monitor.connectionFailed')}
                    </p>
                    <p className="mt-1 text-xs text-text-muted">
                      {monitor.error}
                    </p>
                    <button
                      onClick={() => monitor.monitor(callId)}
                      className="mt-3 rounded-lg bg-error px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-error/80"
                    >
                      {t('common:actions.retry')}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Phone dial-in */}
        {joinMethod === 'phone' && (
          <div className="mt-6 space-y-4">
            <div className="rounded-lg bg-surface-elevated px-4 py-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-text-secondary">
                  {t('monitor.step1Phone')}
                </label>
                <div className="mt-1.5 flex items-center gap-2 rounded-md bg-surface-card px-3 py-2">
                  <Phone className="h-4 w-4 text-text-muted" />
                  <span className="flex-1 font-mono text-sm text-text-primary">
                    {monitor.dialInNumber || t('monitor.loading')}
                  </span>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-text-secondary">
                  {t('monitor.step2Phone')}
                </label>
                <div className="mt-1.5 flex items-center gap-2 rounded-md bg-surface-card px-3 py-2">
                  <span className="flex-1 font-mono text-xs text-text-primary break-all">
                    {monitor.conferenceName || t('monitor.loadingConference')}
                  </span>
                  <button
                    onClick={handleCopyConferenceName}
                    className="rounded p-1 text-text-muted transition-colors hover:bg-surface-elevated hover:text-text-primary"
                    disabled={!monitor.conferenceName}
                  >
                    <Copy className="h-4 w-4" />
                  </button>
                </div>
                {copied && (
                  <p className="mt-1 text-xs text-success">{t('monitor.copied')}</p>
                )}
              </div>
            </div>

            <div className="rounded-lg bg-info/10 px-4 py-3 border border-info/20">
              <p className="text-xs text-text-secondary">
                {t('monitor.phoneJoinNote')}
              </p>
            </div>
          </div>
        )}

        {/* Info */}
        <div className="mt-6 rounded-lg bg-surface-elevated px-3 py-2.5">
          <p className="text-xs text-text-muted">
            {t('monitor.footer')}
          </p>
        </div>
      </div>
    </div>
  )
}
